import React, { useState, useRef, useEffect } from 'react';

const PropertyInvestment = () => {
  const [activeImage, setActiveImage] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal-visible');
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const propertyData = [
    {
      id: 1,
      title: "Capital of Africa",
      subtitle: "Only Continental Capital",
      image: "/images/propertyinvestment/African Union Head Quarter,Addis Ababa Ethiopia.jpg",
      description: "The ONLY city worldwide serving as permanent capital of an entire continent. Home to African Union HQ."
    },
    {
      id: 4,
      title: "UN Economic HQ",
      subtitle: "Policy Center",
      image: "/images/propertyinvestment/Ethiopian Museum of Science.jpg",
      description: "Hosts UN Economic Commission for Africa. Continent's economic policy headquarters."
    }
  ];

  const fallbackImages = [
    "https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=800&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1464146072230-91cabc968266?w=800&auto=format&fit=crop"
  ];

  return (
    <>
      <style>
        {`
          /* ===== Entrance Effect ===== */
          .reveal {
            opacity: 0;
            transform: translateY(40px);
            transition: opacity 0.9s ease, transform 0.9s ease;
          }

          .reveal-visible {
            opacity: 1;
            transform: translateY(0);
          }

          .property-investment {
            background: linear-gradient(135deg, #0a0f1c 0%, #0c1120 100%);
            min-height: 100vh;
            padding: 6rem 1rem;
            position: relative;
            overflow: hidden;
            visibility: visible;
          }

          .investment-container {
            max-width: 1400px;
            margin: 0 auto;
            position: relative;
            z-index: 2;
          }

          .section-header {
            text-align: center;
            margin-bottom: 4rem;
            opacity: 1;
            visibility: visible;
          }

          .section-pre-title {
            color: #f3b33d;
            font-size: 0.9rem;
            font-weight: 400;
            letter-spacing: 0.2em;
            text-transform: uppercase;
            margin-bottom: 1rem;
            display: block;
            font-family: 'Comfortaa', cursive;
          }

          .section-header h2 {
            color: #ffffff;
            font-size: 2.8rem;
            font-weight: 500;
            margin-bottom: 1rem;
            line-height: 1.2;
            font-family: 'Cormorant Garamond', serif;
          }

          .section-header .accent {
            color: #f3b33d;
            font-weight: 600;
          }

          .section-subtitle {
            color: #9aa7b2;
            font-size: 1.1rem;
            max-width: 500px;
            margin: 0 auto;
            line-height: 1.5;
            font-family: 'Comfortaa', cursive;
          }

          .properties-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 1.5rem;
            max-width: 800px;
            margin: 0 auto;
          }

          .property-card {
            position: relative;
            border-radius: 16px;
            overflow: hidden;
            height: 500px;
            transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
            cursor: pointer;
            border: 1px solid rgba(255, 255, 255, 0.08);
          }

          .property-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          }

          .property-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(
              to bottom,
              transparent 0%,
              rgba(0, 0, 0, 0.1) 30%,
              rgba(0, 0, 0, 0.8) 100%
            );
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            padding: 2rem;
            opacity: 0;
            transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          }

          .property-content {
            transform: translateY(20px);
            transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s;
          }

          .property-title {
            color: #ffffff;
            font-size: 1.5rem;
            font-weight: 600;
            margin-bottom: 0.5rem;
            font-family: 'Cormorant Garamond', serif;
          }

          .property-subtitle {
            color: #f3b33d;
            font-size: 0.85rem;
            font-weight: 500;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            margin-bottom: 1rem;
            font-family: 'Comfortaa', cursive;
          }

          .property-description {
            color: #cfd9e0;
            font-size: 0.9rem;
            line-height: 1.5;
            font-family: 'Comfortaa', cursive;
            display: none;
          }

          /* Desktop Hover Effects */
          @media (min-width: 769px) {
            .property-card:hover {
              transform: translateY(-10px);
              border-color: rgba(243, 179, 61, 0.3);
              box-shadow:
                0 20px 40px rgba(0, 0, 0, 0.4),
                0 0 0 1px rgba(243, 179, 61, 0.1);
            }

            .property-card:hover .property-image {
              transform: scale(1.05);
            }

            .property-card:hover .property-overlay {
              opacity: 1;
            }

            .property-card:hover .property-content {
              transform: translateY(0);
            }

            .property-card:hover .property-description {
              display: block;
            }
          }

          /* Mobile Styles */
          @media (max-width: 768px) {
            .property-investment {
              padding: 3rem 1rem;
              min-height: auto;
            }

            .properties-grid {
              grid-template-columns: 1fr;
              gap: 2rem;
            }

            .property-card {
              height: auto;
            }

            .property-image {
              height: 200px;
              border-radius: 16px 16px 0 0;
            }

            .property-overlay {
              position: relative;
              padding: 1.5rem;
              background: rgba(10, 15, 28, 0.95);
              opacity: 1;
              border-radius: 0 0 16px 16px;
            }

            .property-content {
              transform: translateY(0);
            }

            .property-description {
              display: block !important;
              color: #9aa7b2;
            }

            .section-header h2 {
              font-size: 2.2rem;
            }
          }
        `}
      </style>

      <section
        ref={sectionRef}
        className="property-investment reveal"
        id="investment"
      >
        <div className="investment-container">
          <div className="section-header">
            <span className="section-pre-title">Prime Real Estate</span>
            <h2>
              Invest in <span className="accent">Africa's Capital</span>
            </h2>
            <p className="section-subtitle">
              Strategic advantages that make Addis Ababa real estate uniquely valuable
            </p>
          </div>

          <div className="properties-grid">
            {propertyData.map((property, index) => (
              <div
                key={property.id}
                className="property-card"
                onMouseEnter={() => setActiveImage(property.id)}
                onMouseLeave={() => setActiveImage(null)}
              >
                <img
                  src={property.image}
                  alt={property.title}
                  className="property-image"
                  onError={(e) => {
                    e.target.src = fallbackImages[index];
                  }}
                />

                <div className="property-overlay">
                  <div className="property-content">
                    <h3 className="property-title">{property.title}</h3>
                    <div className="property-subtitle">{property.subtitle}</div>
                    <p className="property-description">{property.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default PropertyInvestment;
